/**
 * Rendering an answer.
 *
 * THIS IS A SECURITY SURFACE, not a formatting concern. Ingested documents are
 * untrusted input, prompt injection is in the threat model, and the retrieved text
 * the model was shown is already flagged untrusted by the API. So:
 *
 *   - `rehype-sanitize` with an explicit allowlist
 *   - NO `rehype-raw`, NO `dangerouslySetInnerHTML`, ever
 *   - a document containing `<img onerror=…>` renders as literal text
 *
 * Citation markers become React nodes through a REMARK PLUGIN operating on mdast
 * text nodes, not a regex over the rendered HTML. That is what stops a `[P1]` inside
 * a code span or inside a quoted legal excerpt from being turned into a live chip
 * that points at a passage the excerpt never cited.
 */
import { memo, useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { visit } from "unist-util-visit";
import type { Link, Parent, Root, Text } from "mdast";

import { clsx } from "@/lib/clsx.ts";
import type { V2Mark } from "@/lib/queryV2.ts";

import { remarkV2Marks, type V2MarkLabel } from "./v2ClaimMarks.ts";

/** `[P1]`…`[P9999]`. Bounded to 1-4 digits so `[Pasal 3]` is never touched. */
const PASSAGE_MARKER = /\[P(\d{1,4})\]/g;

/**
 * A legal reference written in the answer's own prose: "Pasal 19", "Pasal 1 angka 11",
 * "Pasal 4 ayat (2)". The model cites this way naturally, far more often than it emits
 * a `[P1]` marker, and until now those were dead text — the reader could see the claim
 * name its article and had no way to reach it.
 *
 * THIS IS NOT A SEMANTIC JUDGEMENT and does not breach the "no string matching" rule.
 * It answers "is this substring the identifier of a citation we are already holding",
 * a structural lookup against `citation.legal_reference`. Nothing is inferred about
 * MEANING, and a reference with no matching citation is left as plain text rather than
 * linked to a guess.
 */
const LEGAL_REFERENCE = /\bPasal\s+\d+[A-Z]?(?:\s+(?:ayat|angka|huruf)\s+\(?[\w]+\)?)?/gi;

/**
 * The DOCUMENT an answer names in its own prose: "Qanun Aceh Nomor 7 Tahun 2019",
 * "Undang-Undang Nomor 14 Tahun 2008", "Pergub Aceh 67/2019".
 *
 * Pasal-level linking alone almost never fires, and the reason is not a matching bug:
 * the model cites the article that CONTAINS the rule ("Pasal 4"), while the retrieved
 * chunks carry whichever articles the retriever returned ("Pasal 25", "Pasal 50").
 * Measured on a real answer: four references in the prose, two citations held, zero
 * overlap, zero chips. A citation apparatus whose links never appear is not one.
 *
 * The document name is the reference that DOES coincide, because the answer opens by
 * naming the instrument it is reading. Still structural, still no guessing: it links
 * only when a citation we hold is that document.
 */
const DOCUMENT_REFERENCE =
  /\b(?:Qanun(?:\s+Aceh)?|Undang-Undang|Peraturan\s+Gubernur|Pergub(?:\s+Aceh)?|Perpres|Permendagri)\s+(?:Nomor\s+)?\d+[/\s]*(?:Tahun\s+)?\d{4}/gi;

const SCHEMA = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []).filter((tag) => tag !== "img" && tag !== "input"),
    "span",
  ],
  attributes: {
    ...defaultSchema.attributes,
    // `title` and `dataVerdict` carry the /query/v2 claim marks (`v2ClaimMarks.ts`).
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ["className"],
      ["dataPassage"],
      ["dataVerdict"],
      ["title"],
    ],
    td: [...(defaultSchema.attributes?.td ?? []), ["className"]],
    th: [...(defaultSchema.attributes?.th ?? []), ["className"]],
  },
};

/**
 * Turns `[P3]` into a `<span data-passage="3">` at the AST level. Code spans and code
 * blocks are skipped by construction: `visit` only reaches `text` nodes, and the
 * contents of `inlineCode`/`code` are not `text` nodes in mdast.
 */
function remarkPassageMarkers() {
  return (tree: Root) => {
    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || index === undefined) return;
      const value = node.value;
      PASSAGE_MARKER.lastIndex = 0;
      if (!PASSAGE_MARKER.test(value)) return;

      PASSAGE_MARKER.lastIndex = 0;
      const children: Array<Text | Link> = [];
      let cursor = 0;
      let match: RegExpExecArray | null;
      while ((match = PASSAGE_MARKER.exec(value)) !== null) {
        if (match.index > cursor) {
          children.push({ type: "text", value: value.slice(cursor, match.index) });
        }
        // A LINK node, not raw HTML. The first version injected
        // `<span class="chip" …>` as mdast `html`; `rehype-sanitize` stripped the
        // element AND its text, so a marker did not merely fail to link — it DELETED
        // ITSELF FROM THE ANSWER. A link survives sanitisation with its text intact,
        // and `components.a` below turns it into the chip.
        children.push(citationLink(match[1] ?? "", match[0]));
        cursor = match.index + match[0].length;
      }
      if (cursor < value.length) children.push({ type: "text", value: value.slice(cursor) });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mdast children are loosely typed
      (parent as any).children.splice(index, 1, ...children);
      return index + children.length;
    });
  };
}

/**
 * Turns "Pasal 19" in the prose into a chip pointing at the citation that carries that
 * `legal_reference`. Operates on mdast TEXT nodes, so a reference inside a code span or
 * an already-linked chip is untouched — and only links when a matching citation exists.
 */
function remarkLegalReferences(references?: Map<string, number>) {
  return (tree: Root) => {
    if (!references || references.size === 0) return;
    // Both patterns in one pass, so a document name and a pasal in the same sentence
    // do not fight over the same text node.
    const pattern = new RegExp(
      `${DOCUMENT_REFERENCE.source}|${LEGAL_REFERENCE.source}`,
      "gi",
    );
    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || index === undefined) return;
      const value = node.value;
      pattern.lastIndex = 0;
      if (!pattern.test(value)) return;

      pattern.lastIndex = 0;
      const children: Array<Text | Link> = [];
      let cursor = 0;
      let match: RegExpExecArray | null;
      while ((match = pattern.exec(value)) !== null) {
        const key = match[0].replace(/\s+/g, " ").trim().toLowerCase();
        const target = references.get(key);
        if (target === undefined) continue;
        if (match.index > cursor) {
          children.push({ type: "text", value: value.slice(cursor, match.index) });
        }
        // The WORDS stay exactly as the model wrote them, and a small numeric marker
        // follows. Replacing "Qanun Aceh Nomor 7 Tahun 2019" with a highlighted chip
        // put a heavy block mid-sentence and wrecked the reading line — and an earlier
        // version dropped the words entirely. The number is how a citation apparatus
        // marks a reference: small, after the phrase, pointing at the rail.
        children.push({ type: "text", value: match[0] });
        children.push(citationLink(String(target), String(target), "chip-ref"));
        cursor = match.index + match[0].length;
      }
      if (children.length === 0) return;
      if (cursor < value.length) children.push({ type: "text", value: value.slice(cursor) });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mdast children are loosely typed
      (parent as any).children.splice(index, 1, ...children);
      return index + children.length;
    });
  };
}

/**
 * One published claim's marks: its text as the answer carries it, and the rail card(s)
 * holding the exact passage(s) that support it — from the response's claim map (#532),
 * never re-derived from document names.
 */
export interface ClaimMark {
  text: string;
  indexes: number[];
}

/**
 * A claim's text as a findable pattern: exact words, any whitespace between them.
 *
 * NOT a semantic judgement — this answers "where in the prose is this exact claim
 * string", an identity lookup modulo whitespace/case (the same equivalence the gate's
 * own `_flat` uses for EQUALITY, never meaning). A claim that does not occur verbatim
 * gets NO marker rather than a guessed placement.
 */
function claimPattern(text: string): RegExp | null {
  const tokens = text
    .replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, "")
    .split(/[ \t\n\r\f\v]+/)
    .filter(Boolean);
  if (tokens.length === 0) return null;
  const source = tokens
    .map((token) => asciiCase(token).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("[ \\t\\n\\r\\f\\v]+");
  try {
    return new RegExp(source, "g");
  } catch {
    return null;
  }
}

/**
 * Appends each claim's citation chip(s) right after the claim's own text (#532).
 *
 * This is the AUTHORITATIVE marker: the chip numbers come from the claim map's resolved
 * citations, so two claims supported by two chunks of the SAME document each open their
 * own passage — the case the document-name linker below structurally cannot represent.
 * Operates on mdast TEXT nodes like the other linkers, so a claim quoted inside a code
 * span is untouched. Each claim marks its FIRST occurrence only; a multi-passage claim
 * gets one chip per supporting card, in citation order.
 */
function remarkClaimMarkers(claims?: ClaimMark[]) {
  return (tree: Root) => {
    if (!claims || claims.length === 0) return;
    const pending = claims
      .map((claim) => ({ ...claim, pattern: claimPattern(claim.text), placed: false }))
      .filter((claim) => claim.pattern !== null && claim.indexes.length > 0);
    if (pending.length === 0) return;
    visit(tree, (node) => {
      if (!isInlineBlock(node)) return;
      const spans = inlineTextSpans(node);
      const value = asciiCase(spans.map((span) => span.value).join(""));
      const cuts = new Map<Text, Array<{ at: number; indexes: number[] }>>();
      const linkCuts = new Map<Link, Array<{ indexes: number[] }>>();
      for (const claim of pending) {
        if (claim.placed) continue;
        if (!claim.pattern) continue;
        claim.pattern.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = claim.pattern.exec(value)) !== null) {
          const start = match.index;
          const end = match.index + match[0].length;
          if (
            spans.some(
              (span) => span.barrier && span.start < end && start < span.end,
            )
          ) {
            continue;
          }
          const target = [...spans].reverse().find((span) => span.start < end && end <= span.end);
          if (!target || target.synthetic) continue;
          if (target.link && end < target.link.end) continue;
          claim.placed = true;
          if (target.link) {
            const existing = linkCuts.get(target.link.node) ?? [];
            existing.push({ indexes: claim.indexes });
            linkCuts.set(target.link.node, existing);
          } else {
            const existing = cuts.get(target.node) ?? [];
            existing.push({ at: end - target.start, indexes: claim.indexes });
            cuts.set(target.node, existing);
          }
          break;
        }
      }
      for (const [textNode, nodeCuts] of cuts) {
        insertClaimLinks(node, textNode, nodeCuts);
      }
      for (const [linkNode, nodeCuts] of linkCuts) {
        insertLinksAfter(node, linkNode, nodeCuts);
      }
    });
  };
}

function asciiCase(text: string): string {
  return text.replace(/[A-Z]/g, (letter) => letter.toLowerCase());
}

function isInlineBlock(node: unknown): node is Parent {
  const type = (node as { type?: unknown })?.type;
  return type === "paragraph" || type === "heading" || type === "tableCell";
}

function inlineTextSpans(parent: Parent): Array<{
  node: Text;
  value: string;
  start: number;
  end: number;
  synthetic?: boolean;
  barrier?: boolean;
  link?: { node: Link; end: number };
}> {
  const spans: Array<{
    node: Text;
    value: string;
    start: number;
    end: number;
    synthetic?: boolean;
    barrier?: boolean;
    link?: { node: Link; end: number };
  }> = [];
  let cursor = 0;
  const walk = (node: Parent, activeLink?: Link): void => {
    for (const child of node.children) {
      if (child.type === "text") {
        spans.push({
          node: child,
          value: child.value,
          start: cursor,
          end: cursor + child.value.length,
          link: activeLink ? { node: activeLink, end: -1 } : undefined,
        });
        cursor += child.value.length;
      } else if (child.type === "break") {
        spans.push({
          node: { type: "text", value: " " },
          value: " ",
          start: cursor,
          end: cursor + 1,
          synthetic: true,
        });
        cursor += 1;
      } else if (child.type === "inlineCode") {
        const value = child.value || "\0";
        spans.push({
          node: { type: "text", value },
          value,
          start: cursor,
          end: cursor + value.length,
          synthetic: true,
          barrier: true,
          link: activeLink ? { node: activeLink, end: -1 } : undefined,
        });
        cursor += value.length;
      } else if (child.type === "link") {
        const first = spans.length;
        walk(child, child);
        for (const span of spans.slice(first)) {
          const link = span.link;
          if (link?.node === child) link.end = cursor;
        }
      } else if ("children" in child) {
        walk(child as Parent, activeLink);
      }
    }
  };
  walk(parent);
  return spans;
}

function insertLinksAfter(
  parent: Parent,
  target: Link,
  cuts: Array<{ indexes: number[] }>,
): boolean {
  const index = parent.children.indexOf(target);
  if (index >= 0) {
    const links = cuts.flatMap((cut) =>
      cut.indexes.map((passage) => citationLink(String(passage), String(passage), "chip-ref")),
    );
    parent.children.splice(index + 1, 0, ...links);
    return true;
  }
  for (const child of parent.children) {
    if (child.type !== "inlineCode" && child.type !== "link" && "children" in child) {
      if (insertLinksAfter(child as Parent, target, cuts)) return true;
    }
  }
  return false;
}

function insertClaimLinks(
  parent: Parent,
  target: Text,
  cuts: Array<{ at: number; indexes: number[] }>,
): boolean {
  const index = parent.children.indexOf(target);
  if (index >= 0) {
    cuts.sort((a, b) => a.at - b.at);
    const children: Array<Text | Link> = [];
    let cursor = 0;
    for (const cut of cuts) {
      if (cut.at > cursor) children.push({ type: "text", value: target.value.slice(cursor, cut.at) });
      for (const passage of cut.indexes) {
        children.push(citationLink(String(passage), String(passage), "chip-ref"));
      }
      cursor = Math.max(cursor, cut.at);
    }
    if (cursor < target.value.length) children.push({ type: "text", value: target.value.slice(cursor) });
    parent.children.splice(index, 1, ...children);
    return true;
  }
  for (const child of parent.children) {
    if (child.type !== "inlineCode" && child.type !== "link" && "children" in child) {
      if (insertClaimLinks(child as Parent, target, cuts)) return true;
    }
  }
  return false;
}

/**
 * The one shape both linkers emit: an mdast link to `#ev-<n>`.
 *
 * It must be a node the sanitiser keeps. Raw HTML was stripped along with its text,
 * which quietly rewrote answers — the reason this exists rather than a span.
 */
function citationLink(target: string, text: string, kind = "chip"): Link {
  return {
    type: "link",
    url: `#ev-${target}`,
    title: kind,
    children: [{ type: "text", value: text }],
  };
}

/** A numeric-looking cell is right-aligned. Detection is on CONTENT, never on the
 *  header text, and the digits themselves are never rewritten. */
function isNumeric(text: string): boolean {
  return /^[\s\d.,%()+-]*\d[\s\d.,%()+-]*$/.test(text.trim()) && /\d/.test(text);
}

function textOf(node: unknown): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  const props = (node as { props?: { children?: unknown } } | null)?.props;
  return props ? textOf(props.children) : "";
}

export const AnswerMarkdown = memo(function AnswerMarkdown({
  children,
  onPassageClick,
  litPassage,
  references,
  claims,
  v2Marks,
  v2Label,
}: {
  children: string;
  onPassageClick?: (passage: number) => void;
  litPassage?: number | null;
  /** `legal_reference` (lower-cased, whitespace-collapsed) -> evidence-rail number.
   *  Navigation only; `claims` is the claim's actual provenance (#532). */
  references?: Map<string, number>;
  /** The claim-to-passage marks resolved from the response's claim map. */
  claims?: ClaimMark[];
  /** `/query/v2` verdict marks, by UTF-16 offset into `children` (the Markdown source). */
  v2Marks?: V2Mark[];
  v2Label?: (mark: V2Mark) => V2MarkLabel;
}) {
  const v2Options = useMemo(
    () => (v2Marks && v2Label ? { marks: v2Marks, label: v2Label } : undefined),
    [v2Marks, v2Label],
  );
  const components = useMemo<Components>(
    () => ({
      table: ({ children: kids }) => (
        <div className="table-scroll">
          <table>{kids}</table>
        </div>
      ),
      td: ({ children: kids }) => (
        <td className={clsx(isNumeric(textOf(kids)) && "num")}>{kids}</td>
      ),
      th: ({ children: kids }) => (
        <th className={clsx(isNumeric(textOf(kids)) && "num")}>{kids}</th>
      ),

      a: ({ children: kids, href, title }) => {
        // Our own citation links, emitted by the two remark plugins above.
        if (href?.startsWith("#ev-")) {
          const index = Number(href.slice(4));
          // A /query/v2 chip: coloured by the claim's verdict, details on hover.
          if (title?.startsWith("chip-v2:")) {
            // Split on the FIRST "|" only: the tooltip is translated prose.
            const spec = title.slice("chip-v2:".length);
            const cut = spec.indexOf("|");
            const verdict = cut < 0 ? spec : spec.slice(0, cut);
            const tooltip = cut < 0 ? "" : spec.slice(cut + 1);
            return (
              <button
                type="button"
                className={clsx("chip", "chip-ref", "chip-v2", `chip-v2-${verdict}`, litPassage === index && "lit")}
                onClick={() => onPassageClick?.(index)}
                title={tooltip}
                aria-label={`Sumber ${index}: ${tooltip}`}
              >
                {kids}
              </button>
            );
          }
          return (
            <button
              type="button"
              className={clsx("chip", title === "chip-ref" && "chip-ref", litPassage === index && "lit")}
              onClick={() => onPassageClick?.(index)}
              aria-label={`Sumber ${index}`}
            >
              {kids}
            </button>
          );
        }
        // Anything else came from document text and is not to be trusted with the tab
        // that holds a session.
        return (
          <a href={href} target="_blank" rel="noopener noreferrer nofollow ugc">
            {kids}
          </a>
        );
      },
    }),
    [litPassage, onPassageClick],
  );

  return (
    <div className="prose">
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          // FIRST after parsing: v2 marks are placed by source offset, and every plugin
          // below replaces text nodes with new ones that carry no position.
          [remarkV2Marks, v2Options],
          remarkPassageMarkers,
          // Claim marks FIRST: they are the authoritative provenance, and running them
          // before the name linker means a claim chip lands on the claim's own text
          // before that text is split around a document-name match.
          [remarkClaimMarkers, claims],
          [remarkLegalReferences, references],
        ]}
        rehypePlugins={[[rehypeSanitize, SCHEMA]]}
        components={components}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});
